import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
// Import needed components
import { List, Button, Modal, Form, Input, message, Spin, Empty, Popconfirm, Space, Alert, Typography, Tooltip, Card, Descriptions } from 'antd'; // Added Card, Descriptions
import MomentDatePicker from './MomentDatePicker.tsx';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, HistoryOutlined } from '@ant-design/icons'; // Added HistoryOutlined
import { useAuth } from '../context/AuthContext';
// Import the NEW form component
import StructuredSurgicalHistoryForm from './StructuredSurgicalHistoryForm'; // Corrected import path if needed

const { TextArea } = Input;
const { Text, Title } = Typography; // Import Title

// Helper to format date
const formatDate = (dateString) => {
    return dateString ? moment(dateString).format('MM/DD/YYYY') : 'N/A';
};

// Helper to render category titles (SYNCED WITH FORM)
const formatCategoryName = (categoryKey) => {
    if (!categoryKey) return '';
    // Add Vietnamese titles for the new categories
    if (categoryKey === 'general') return 'TỔNG QUÁT';
    if (categoryKey === 'neuro_ent_eye') return 'THẦN KINH - TAI MŨI HỌNG - MẮT';
    if (categoryKey === 'cardiac_thoracic') return 'TIM MẠCH - LỒNG NGỰC';
    if (categoryKey === 'gi_hepatobiliary') return 'TIÊU HÓA - GAN MẬT';
    if (categoryKey === 'renal_urology') return 'THẬN - TIẾT NIỆU';
    if (categoryKey === 'orthopedic') return 'CƠ XƯƠNG KHỚP';
    if (categoryKey === 'gynecology_breast') return 'PHỤ KHOA - VÚ';

    // Exclude internal fields
    if (['lastUpdatedAt', 'lastUpdatedBy', '_id'].includes(categoryKey)) return null;

    // Default formatting for 'other' or any future keys
    return categoryKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
};

const SurgicalHistorySection = ({ patientId }) => {
  const [history, setHistory] = useState([]); // For free-text surgical history notes
  const [lastReviewed, setLastReviewed] = useState(null);
  // NEW: State for structured surgical checklist data
  const [structuredSurgicalHistoryData, setStructuredSurgicalHistoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal states for original free-text notes
  const [isItemModalVisible, setIsItemModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);

  // NEW: Modal state for the checklist
  const [isChecklistModalVisible, setIsChecklistModalVisible] = useState(false);

  const { user } = useAuth();

  // Fetch data including structured surgical history
  const fetchHistory = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch patient data populating review user
      // Assuming the populate query is correct or handled by default patient fetch
      const response = await axios.get(`/patients/${patientId}?populate=surgicalHistoryLastReviewed.user`);
      const patientData = response.data.data.patient;
      setHistory(patientData?.surgicalHistory || []); // Free text history
      setLastReviewed(patientData?.surgicalHistoryLastReviewed || null);
      // Set structured SURGICAL history state
      setStructuredSurgicalHistoryData(patientData?.structuredSurgicalHistory || null);
    } catch (err) {
      console.error("Error fetching surgical history data:", err);
      setError(err.response?.data?.message || 'Failed to fetch surgical history data.');
      setHistory([]);
      setLastReviewed(null);
      setStructuredSurgicalHistoryData(null); // Clear on error
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

 // --- Handlers for Original Item Modal (Add/Edit Free Text) ---
  const showItemModal = (item = null) => {
    setEditingItem(item);
    if (item) {
      // Find the item in the current state to pre-fill accurately
      const currentItem = history.find(h => h.procedure === item.procedure && h.procedureDate === item.procedureDate);
      form.setFieldsValue({
        ...currentItem,
        procedureDate: currentItem?.procedureDate ? moment(currentItem.procedureDate) : null,
      });
    } else {
      form.resetFields();
    }
    setIsItemModalVisible(true);
  };

  const handleItemModalCancel = () => {
    setIsItemModalVisible(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleItemFinish = async (values) => {
    // Logic copied from user provided snippet
    setSubmitLoading(true);
    setError(null);
    const formattedValues = { ...values, procedureDate: values.procedureDate ? values.procedureDate.toISOString() : null };
    let updatedHistory;
    if (editingItem) {
      const index = history.findIndex(item => item.procedure === editingItem.procedure && item.procedureDate === editingItem.procedureDate);
      if (index > -1) { updatedHistory = [...history]; updatedHistory[index] = formattedValues; }
      else { message.error("Could not find original item. Please refresh."); setSubmitLoading(false); return; }
    } else { updatedHistory = [...history, formattedValues]; }

    try {
      // Logic copied from user provided snippet
      await axios.patch(`/patients/${patientId}`, { surgicalHistory: updatedHistory });
      message.success(`Surgical history ${editingItem ? 'updated' : 'added'} successfully!`);
      handleItemModalCancel(); // Use correct cancel handler
      setHistory(updatedHistory); // Update local state
    } catch (err) {
      // Logic copied from user provided snippet
      console.error("Error saving surgical history:", err);
      message.error(err.response?.data?.message || 'Failed to save surgical history.');
    } finally {
        // Logic copied from user provided snippet
        setSubmitLoading(false);
     }
  };

  const handleItemDelete = async (itemToDelete) => {
    // Logic copied from user provided snippet
     const updatedHistory = history.filter(item => !(item.procedure === itemToDelete.procedure && item.procedureDate === itemToDelete.procedureDate));
     try {
        setLoading(true);
        await axios.patch(`/patients/${patientId}`, { surgicalHistory: updatedHistory });
        message.success('Surgical history item deleted successfully!');
        setHistory(updatedHistory); // Update local state
     } catch (err) {
        console.error("Error deleting surgical history item:", err);
        message.error(err.response?.data?.message || 'Failed to delete item.');
     } finally {
        setLoading(false);
     }
  };
 // --- End Original Item Modal Handlers ---


  // --- Handlers for NEW Structured Checklist Modal ---
  const showChecklistModal = () => { setIsChecklistModalVisible(true); };
  const handleChecklistModalCancel = () => { setIsChecklistModalVisible(false); };

  const handleChecklistSaveSuccess = () => {
      setIsChecklistModalVisible(false);
      message.success("Surgical Checklist saved successfully!");
      // Refetch all history data to update the display
      fetchHistory();
  };

  // Handle "Mark as Reviewed"
   const handleMarkReviewed = async () => {
     if (!user || !user._id) { message.error("Cannot mark as reviewed: User not logged in."); return; }
     const reviewData = { user: user._id, date: new Date().toISOString(), };
     try {
       setLoading(true);
       await axios.patch(`/patients/${patientId}`, { surgicalHistoryLastReviewed: reviewData });
       message.success('Surgical History marked as reviewed.');
       setLastReviewed({ user: { _id: user._id, name: user.name }, date: reviewData.date });
     } catch (err) {
       console.error("Error marking surgical history as reviewed:", err);
       message.error(err.response?.data?.message || 'Failed to mark as reviewed.');
     } finally { setLoading(false); }
   };

  const canModify = user && ['Admin', 'MD', 'Nursing'].includes(user.role);

   // Helper function to check if any checklist items are selected
  const hasSelectedChecklistItems = (data) => {
      if (!data) return false;
      return Object.entries(data).some(([key, value]) =>
          !['lastUpdatedAt', 'lastUpdatedBy', '_id'].includes(key) && Array.isArray(value) && value.length > 0
      );
  };

  return (
    <Spin spinning={loading}>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: '10px' }}>
        {/* Action Buttons */}
        <Space>
           {/* NEW Checklist Button */}
           {canModify && (<Button type="primary" icon={<HistoryOutlined />} onClick={showChecklistModal}>Update Surgical History Checklist</Button>)}
           {/* Original Add Button for free text */}
           {canModify && (<Button icon={<PlusOutlined />} onClick={() => showItemModal(null)} >Add Surgical Note</Button>)}
           {canModify && (<Tooltip title="Mark this section as reviewed"><Button icon={<CheckCircleOutlined />} onClick={handleMarkReviewed}>Mark as Reviewed</Button></Tooltip>)}
        </Space>
         {/* Display Last Reviewed Status */}
         <div style={{ textAlign: 'right' }}>
             {lastReviewed?.date ? (<Text type="secondary" style={{ fontSize: '0.85em' }}>Last Reviewed by {lastReviewed.user?.name || 'Unknown User'} on {moment(lastReviewed.date).format('L LT')}</Text>
             ) : (<Text type="secondary" style={{ fontSize: '0.85em' }}>Not yet reviewed</Text>)}
         </div>
      </div>

       {/* --- NEW: Display Structured Surgical Checklist Items --- */}
       <Card size="small" title="Surgical History Checklist Summary" style={{ marginBottom: 16, background: '#fafafa' }}>
            {structuredSurgicalHistoryData && hasSelectedChecklistItems(structuredSurgicalHistoryData) ? (
                 Object.entries(structuredSurgicalHistoryData)
                    .filter(([key, value]) => !['lastUpdatedAt', 'lastUpdatedBy', '_id'].includes(key) && Array.isArray(value) && value.length > 0)
                    .map(([categoryKey, procedures]) => {
                        const categoryName = formatCategoryName(categoryKey);
                        return categoryName ? (
                            <Descriptions key={categoryKey} title={<Title level={5} style={{ marginBottom: 0 }}>{categoryName}</Title>} size="small" column={1} style={{ marginBottom: 10 }}>
                                <Descriptions.Item>
                                    <List
                                        size="small"
                                        dataSource={procedures}
                                        renderItem={item => (
                                            <List.Item style={{ borderBottom: 'none', padding: '0' }}> {/* Remove default list item styling */}
                                                <span className="history-tag">{item}</span>
                                            </List.Item>
                                        )}
                                        grid={{ gutter: 0, column: 1 }} // Use grid to allow items to flow naturally if needed, but keep single column for now
                                    />
                                </Descriptions.Item>
                            </Descriptions>
                        ) : null;
                    })
            ) : (
                 <Empty description="No checklist items selected." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
             {structuredSurgicalHistoryData?.lastUpdatedAt && (
                 <div style={{ textAlign: 'right', marginTop: '10px' }}>
                     <Text type="secondary" style={{ fontSize: '0.85em' }}>
                         Checklist Last Updated: {moment(structuredSurgicalHistoryData.lastUpdatedAt).format('L LT')}
                     </Text>
                 </div>
             )}
       </Card>


      {/* List for the original free-text surgical history items */}
       <Title level={5} style={{ marginTop: '20px', marginBottom: '10px' }}>Surgical History Notes</Title>
      <List
        itemLayout="horizontal"
        dataSource={history}
        locale={{ emptyText: <Empty description="No surgical history notes recorded." /> }}
        renderItem={item => (
          <List.Item
            actions={canModify ? [
              <Button icon={<EditOutlined />} onClick={() => showItemModal(item)} size="small" key={`edit-${item.procedure}`} />,
              <Popconfirm key={`delete-${item.procedure}`} title="Are you sure delete this item?" onConfirm={() => handleItemDelete(item)} okText="Yes" cancelText="No" >
                <Button icon={<DeleteOutlined />} danger size="small" />
              </Popconfirm>
            ] : []}
          >
            <List.Item.Meta
              title={item.procedure}
              description={
                <Space direction="vertical" size="small">
                  {item.procedureDate && <Text type="secondary">Date: {formatDate(item.procedureDate)}</Text>}
                  {item.surgeon && <Text type="secondary">Surgeon: {item.surgeon}</Text>}
                  {item.notes && <Text>Notes: {item.notes}</Text>}
                </Space>
              }
            />
          </List.Item>
        )}
      />

      {/* Original Modal for Adding/Editing free-text items */}
      <Modal title={editingItem ? 'Edit Surgical History Note' : 'Add Surgical History Note'} visible={isItemModalVisible} onCancel={handleItemModalCancel} footer={null} destroyOnClose>
         <Spin spinning={submitLoading}>
             <Form form={form} layout="vertical" onFinish={handleItemFinish}>
                 <Form.Item name="procedure" label="Procedure" rules={[{ required: true, message: 'Procedure is required.' }]}>
                     <Input />
                 </Form.Item>
                 <Form.Item name="procedureDate" label="Approximate Procedure Date">
                     <MomentDatePicker style={{ width: '100%' }} />
                 </Form.Item>
                 <Form.Item name="surgeon" label="Surgeon (Optional)">
                     <Input />
                 </Form.Item>
                 <Form.Item name="notes" label="Notes">
                     <TextArea rows={3} />
                 </Form.Item>
                 <Form.Item>
                     <Button type="primary" htmlType="submit" loading={submitLoading}>{editingItem ? 'Save Changes' : 'Add Item'}</Button>
                     <Button style={{ marginLeft: 8 }} onClick={handleItemModalCancel}>Cancel</Button>
                 </Form.Item>
             </Form>
         </Spin>
      </Modal>

      {/* NEW Modal for the Structured Checklist */}
      <Modal title="Update Structured Surgical History Checklist" visible={isChecklistModalVisible} onCancel={handleChecklistModalCancel} footer={null} width={800} destroyOnClose >
          {isChecklistModalVisible && patientId && (
              <StructuredSurgicalHistoryForm // Use the correct form component
                  patientId={patientId}
                  onSaveSuccess={handleChecklistSaveSuccess}
                  onCancel={handleChecklistModalCancel}
              />
          )}
      </Modal>

    </Spin>
  );
};

export default SurgicalHistorySection;
